import classNames from 'classnames';
import { memo, useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import { T } from '../../translation/src';
import { useDebugToolsQuery, useSuperadminFailedPointCloudCountsQuery, useSuperadminInvoiceCountsQuery } from '../../types/graphqlTypes';
import { StatusPill } from './AdminListPage';

const linkClassName = ({ isActive }: { isActive: boolean }) =>
  classNames(
    'flex items-center gap-2 rounded-full border px-3 py-1 transition-colors',
    isActive ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black' : 'border-gray-300 hover:border-gray-500 dark:border-gray-600 dark:hover:border-gray-400',
  );

// The admin center's follow-up lists, each with what needs attention. Shown on the admin center and on every
// list, the one open highlighted, so a superadmin jumps between them without going back first.
const AdminSectionLinks_: React.FC = () => {
  const currentUser = useContext(UserContext);
  const skip = !currentUser?.isSuperAdmin;
  const { data: failedCountsData } = useSuperadminFailedPointCloudCountsQuery({ skip, fetchPolicy: 'cache-and-network' });
  const failedUploadCount = failedCountsData?.superadminFailedPointCloudCounts.failed;
  const { data: invoiceCountsData } = useSuperadminInvoiceCountsQuery({ skip, fetchPolicy: 'cache-and-network' });
  const draftInvoiceCount = invoiceCountsData?.superadminInvoiceCounts.draft;
  const unpaidInvoiceCount = invoiceCountsData?.superadminInvoiceCounts.unpaid;
  // Only where the server has the debug tools on (a developer's machine).
  const { data: debugData } = useDebugToolsQuery({ skip, fetchPolicy: 'cache-first' });

  return (
    <div className="flex items-center gap-2 ml-auto text-sm">
      {debugData?.debugTools.enabled && (
        <NavLink to="/admin-center/debug" className={linkClassName}>
          <T _str="debug tools" swc />
        </NavLink>
      )}
      <NavLink to="/admin-center/invoices" className={linkClassName}>
        <T _str="invoices" swc />
        {!!draftInvoiceCount && <StatusPill tone="orange">{draftInvoiceCount}</StatusPill>}
        {!!unpaidInvoiceCount && <StatusPill tone="red">{unpaidInvoiceCount}</StatusPill>}
      </NavLink>
      <NavLink to="/admin-center/failed-uploads" className={linkClassName}>
        <T _str="failed uploads" swc />
        {!!failedUploadCount && <StatusPill tone="red">{failedUploadCount}</StatusPill>}
      </NavLink>
    </div>
  );
};
export const AdminSectionLinks = memo(AdminSectionLinks_);
